import { useEffect, useState } from 'react';
import { api } from '../../api';
import { Cargando, ErrorConReintento } from '../../ui/EstadoDePantalla';
import { useSubpantalla } from '../../ui/navegacionReact';

/* =====================================================================
   Asistencia · revisión, avisos, rostro y mis registros
   (DIRECTRIZ-TERMINAL-ROSTRO §4, §6, §7 y §8 · carril terminal)
   =====================================================================
   El servidor decide con reglas; aquí una persona confirma o rectifica.
   Nada se edita ni se borra: cada corrección es un registro nuevo con
   motivo, y el original sigue en el rastro.
   ===================================================================== */

const MOTIVOS = {
  FRANJA_MEDIA: 'Se confirmó con un toque', CERCA_DEL_UMBRAL: 'Reconocimiento justo en el límite',
  VIDA_DUDOSA: 'Prueba de persona real dudosa', NO_SOY_YO: 'Alguien tocó «No soy yo»',
  NO_FUI_YO: 'La persona dice «Esto no fui yo»', FUERA_DE_HORARIO: 'Fuera de su horario',
  DOS_TERMINALES: 'En dos terminales casi al mismo tiempo', SIN_SALIDA: 'Entró y no registró salida',
  MUESTREO: 'Revisión al azar de la semana', SEGURIDAD: 'Intento con foto, celular o cara no registrada',
  HORA_NO_CONFIRMADA: 'La terminal no tenía la hora confirmada',
};
const EVENTOS = {
  ENTRADA: 'Entrada', INICIO_COMIDA: 'Salida a comer', FIN_COMIDA: 'Regreso de comer',
  SALIDA_CAMPO: 'Salida a campo', REGRESO_CAMPO: 'Regreso de campo', SALIDA: 'Salida',
};
const BOTON = 'min-h-12 rounded-lg px-4 font-semibold';
const PRIMARIO = `${BOTON} bg-blue-600 text-white disabled:bg-gray-300 disabled:text-gray-600`;
const SECUNDARIO = `${BOTON} border border-gray-300 bg-white text-gray-800`;
const METODOS = { ROSTRO: 'con su rostro', PIN: 'con PIN', QR: 'con el QR', AJUSTE: 'corregido por un encargado' };

function Confirmar({ texto, consecuencia, alConfirmar }) {
  // Una pregunta en línea («¿seguro?»), no una vista: no tapa nada.
  const [preguntando, setPreguntando] = useState(false);
  if (!preguntando) return <button type="button" className={SECUNDARIO} onClick={() => setPreguntando(true)}>{texto}</button>;
  return (
    <span className="flex flex-col gap-2 rounded-lg border border-red-300 bg-red-50 p-2">
      <span className="text-sm text-red-900">{consecuencia}</span>
      <span className="flex gap-2">
        <button type="button" className={`${BOTON} bg-red-700 text-white`} onClick={() => { setPreguntando(false); alConfirmar(); }}>Sí, {texto.toLowerCase()}</button>
        <button type="button" className={SECUNDARIO} onClick={() => setPreguntando(false)}>Cancelar</button>
      </span>
    </span>
  );
}

function uuid() {
  return globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function useLeer(ruta) {
  const [n, setN] = useState(0);
  const clave = `${ruta}#${n}`;
  const [r, setR] = useState({ clave: null, d: null, error: null });
  useEffect(() => {
    let vivo = true;
    api.get(ruta)
      .then((x) => vivo && setR({ clave, d: x, error: null }))
      .catch((e) => vivo && setR({ clave, d: null, error: e }));
    return () => { vivo = false; };
  }, [ruta, n, clave]);
  const vigente = r.clave === clave;
  return { d: vigente ? r.d : null, error: vigente ? r.error : null, recargar: () => setN((x) => x + 1) };
}

function Marco({ d, error, recargar, que, children }) {
  if (error) return <ErrorConReintento que={`No se pudo cargar ${que}`} porque={error.message} onReintentar={recargar} />;
  if (!d) return <Cargando filas={3} etiqueta={`Cargando ${que}`} />;
  return children;
}


/* --- La foto del registro junto a la de referencia (§6.3) ------------- */
function Fotos({ idEvento, idUsuario }) {
  const [urls, setUrls] = useState(null);
  const [msg, setMsg] = useState(null);
  async function ver() {
    try {
      const [e, r] = await Promise.allSettled([
        api.bajar(`/asistencia/eventos/${idEvento}/evidencia`),
        api.bajar(`/asistencia/personas/${idUsuario}/rostro/referencia`)]);
      setUrls({ e: e.status === 'fulfilled' ? URL.createObjectURL(e.value) : null,
                r: r.status === 'fulfilled' ? URL.createObjectURL(r.value) : null });
      if (e.status !== 'fulfilled') setMsg(e.reason?.message);
    } catch (x) { setMsg(x.message); }
  }
  if (!urls) {
    return (
      <div className="space-y-1">
        <button type="button" className={SECUNDARIO} onClick={ver}>Ver la foto del registro</button>
        <p className="text-xs text-gray-600">Cada vez que alguien ve una foto queda anotado quién y cuándo.</p>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-2 gap-2">
      <figure className="space-y-1 text-center text-xs text-gray-600">
        {urls.e ? <img src={urls.e} alt="Cara en el momento del registro" className="mx-auto h-40 w-40 rounded-lg object-cover" />
          : <p className="rounded-lg border border-dashed p-4">{msg || 'Sin foto'}</p>}
        <figcaption>En el registro</figcaption>
      </figure>
      <figure className="space-y-1 text-center text-xs text-gray-600">
        {urls.r ? <img src={urls.r} alt="Foto de referencia de la persona" className="mx-auto h-40 w-40 rounded-lg object-cover" />
          : <p className="rounded-lg border border-dashed p-4">Sin foto de referencia</p>}
        <figcaption>De referencia</figcaption>
      </figure>
    </div>
  );
}

/* --- Rectificar (§7) ----------------------------------------------------- */
export function Rectificar({ idUsuario, idEvento, alTerminar }) {
  const personas = useLeer('/asistencia/rostro/personas');
  const [tipo, setTipo] = useState(idEvento ? 'ANULAR' : 'AGREGAR');
  const [motivo, setMotivo] = useState('');
  const [otra, setOtra] = useState('');
  const [evento, setEvento] = useState('ENTRADA');
  const [hora, setHora] = useState('');
  const [msg, setMsg] = useState(null);
  const pideHora = tipo === 'AGREGAR' || tipo === 'CORREGIR_HORA';
  const pideEvento = tipo === 'AGREGAR' || tipo === 'CAMBIAR_EVENTO';
  const falta = motivo.trim().length < 3 ? 'Escribe el motivo (al menos 3 letras).'
    : pideHora && !hora ? 'Escribe la hora del registro.'
      : tipo === 'REASIGNAR' && !otra ? 'Elige a quién era de verdad el registro.' : null;
  async function enviar() {
    const cuerpo = { tipo, id_usuario: idUsuario, motivo, uuid_cliente: uuid() };
    if (idEvento && tipo !== 'AGREGAR') cuerpo.ajusta_a = idEvento;
    if (pideEvento) cuerpo.evento = evento;
    if (pideHora) cuerpo.ocurrio_en = new Date(hora).toISOString();
    if (tipo === 'REASIGNAR') cuerpo.id_usuario_nuevo = Number(otra);
    try {
      const r = await api.post('/asistencia/rectificaciones', cuerpo);
      setMsg(r.mensaje);
      alTerminar?.();
    } catch (e) { setMsg(`${e.message} ${e.queHacer || ''}`); }
  }
  return (
    <div className="space-y-2 rounded-lg border border-gray-200 bg-gray-50 p-3" role="dialog" aria-label="Rectificar registro">
      <p className="text-sm text-gray-700">El registro original se conserva. Si cambia horas trabajadas, lo firma otra persona con permiso y se aplica solo al aprobarse.</p>
      <label className="block text-sm font-medium text-gray-800">Qué hay que corregir
        <select value={tipo} onChange={(e) => setTipo(e.target.value)} className="mt-1 min-h-12 w-full rounded-lg border px-3">
          {idEvento && <option value="ANULAR">No fue esta persona: anular</option>}
          {idEvento && <option value="REASIGNAR">Fue otra persona: reasignar</option>}
          {idEvento && <option value="CAMBIAR_EVENTO">Era otro evento</option>}
          {idEvento && <option value="CORREGIR_HORA">La hora está mal</option>}
          <option value="AGREGAR">Falta un registro: agregar</option>
        </select>
      </label>
      {tipo === 'REASIGNAR' && (
        <label className="block text-sm font-medium text-gray-800">Quién fue
          <select value={otra} onChange={(e) => setOtra(e.target.value)} className="mt-1 min-h-12 w-full rounded-lg border px-3">
            <option value="">Elige a la persona</option>
            {(personas.d?.personas || []).filter((p) => p.activo && p.id !== idUsuario).map((p) => (
              <option key={p.id} value={p.id}>{p.nombre}</option>))}
          </select>
        </label>
      )}
      {pideEvento && (
        <label className="block text-sm font-medium text-gray-800">Evento
          <select value={evento} onChange={(e) => setEvento(e.target.value)} className="mt-1 min-h-12 w-full rounded-lg border px-3">
            {Object.entries(EVENTOS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </label>
      )}
      {pideHora && (
        <label className="block text-sm font-medium text-gray-800">Fecha y hora
          <input type="datetime-local" value={hora} onChange={(e) => setHora(e.target.value)} className="mt-1 min-h-12 w-full rounded-lg border px-3" />
        </label>
      )}
      <input value={motivo} onChange={(e) => setMotivo(e.target.value)} placeholder="Motivo (obligatorio)"
        className="min-h-12 w-full rounded-lg border px-3" />
      <button type="button" disabled={!!falta} onClick={enviar} className={`${PRIMARIO} w-full`}>Rectificar</button>
      {falta && <p className="text-sm text-gray-600">{falta}</p>}
      {msg && <p role="status" className="text-sm">{msg}</p>}
    </div>
  );
}

/* --- Una tarjeta de revisión con sus tres botones (§6.4 y §8) ------------- */
function TarjetaRevision({ r, alCambiar }) {
  const [abierto, setAbierto] = useState(null);
  // «Es un error» y «Rectificar» abren un panel: atrás lo cierra, no sale de Asistencia.
  useSubpantalla(`revision-${r.id}`, !!abierto, () => setAbierto(null));
  const [nota, setNota] = useState('');
  const [msg, setMsg] = useState(null);
  async function resolver(resolucion) {
    try {
      const x = await api.post(`/asistencia/revision/${r.id}/resolver`, { resolucion, nota: nota || null });
      setMsg(x.mensaje);
      alCambiar();
    } catch (e) { setMsg(`${e.message} ${e.queHacer || ''}`); }
  }
  const alto = r.prioridad === 1;
  return (
    <li className={`space-y-2 rounded-xl border p-3 ${alto ? 'border-red-300 bg-red-50' : 'border-amber-200 bg-amber-50'}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <span className="font-semibold text-gray-900">{r.persona || 'Sin persona'}</span>
        <span className="text-sm text-gray-700">{r.hora || ''}{r.terminal ? ` · ${r.terminal}` : ''}</span>
      </div>
      <p className="text-sm text-gray-800">{MOTIVOS[r.motivo] || r.motivo}
        {r.evento ? ` · ${EVENTOS[r.evento] || r.evento}` : ''}{r.metodo ? ` ${METODOS[r.metodo] || ''}` : ''}
        {r.certeza != null ? ` · certeza ${Math.round(Number(r.certeza) * 100)} %` : ''}</p>
      {r.detalle?.hora_estimada && <p className="text-sm text-gray-700">Hora estimada: {new Date(r.detalle.hora_estimada).toLocaleString('es-MX')}</p>}
      {r.con_evidencia && r.id_evento && r.id_usuario && <Fotos idEvento={r.id_evento} idUsuario={r.id_usuario} />}
      <div className="grid grid-cols-3 gap-2">
        <button type="button" className={SECUNDARIO} onClick={() => resolver('CORRECTO')}>Correcto</button>
        <button type="button" className={SECUNDARIO} onClick={() => setAbierto(abierto === 'error' ? null : 'error')}>Es un error</button>
        {r.id_usuario ? <button type="button" className={SECUNDARIO} onClick={() => setAbierto(abierto === 'rect' ? null : 'rect')}>Rectificar</button>
          : <span className="self-center text-xs text-gray-600">Sin registro que rectificar</span>}
      </div>
      {abierto === 'error' && (
        <div className="space-y-2">
          <input value={nota} onChange={(e) => setNota(e.target.value)} placeholder="Qué estuvo mal (obligatorio)" className="min-h-12 w-full rounded-lg border px-3" />
          <button type="button" disabled={nota.trim().length < 3} onClick={() => resolver('ERROR')} className={`${PRIMARIO} w-full`}>Marcar como error</button>
          {nota.trim().length < 3 && <p className="text-sm text-gray-600">Escribe una frase de qué estuvo mal: queda en el rastro.</p>}
        </div>
      )}
      {abierto === 'rect' && <Rectificar idUsuario={r.id_usuario} idEvento={r.id_evento} alTerminar={() => resolver('RECTIFICADO')} />}
      {msg && <p role="status" className="text-sm">{msg}</p>}
    </li>
  );
}

export function Revision() {
  const b = useLeer('/asistencia/revision');
  return (
    <Marco {...b} que="la bandeja de revisión">
      {b.d && (
        <div className="space-y-3">
          <p className="text-sm text-gray-600">Aquí entra solo lo dudoso. Primero lo que la persona marcó como «no fui yo».</p>
          {b.d.revisiones.length === 0 && (
            <div className="rounded-xl border border-dashed p-6 text-center text-gray-600">
              No hay nada que revisar. Cuando un registro sea dudoso, aparece aquí con sus botones.
            </div>
          )}
          <ul className="space-y-2">{b.d.revisiones.map((r) => <TarjetaRevision key={r.id} r={r} alCambiar={b.recargar} />)}</ul>
        </div>
      )}
    </Marco>
  );
}

export function Avisos() {
  const c = useLeer('/asistencia/centro');
  return (
    <Marco {...c} que="los avisos">
      {c.d && (
        <div className="space-y-3">
          <p className="text-sm text-gray-600">Lo que pasó fuera de lo normal, para confirmarlo o corregirlo. Es interno de la empresa.</p>
          {c.d.camaras.map((x, i) => (
            <p key={i} className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm">
              {x.tipo === 'CAMARA_REPUESTA' ? 'Cámara repuesta' : x.tipo === 'CAMARA_TAPADA' ? 'Cámara tapada' : 'Cámara desconectada'}
              {` en ${x.terminal || 'una terminal'} · ${new Date(x.ocurrio_en).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}`}
            </p>))}
          {c.d.inmediatos.length === 0 && c.d.camaras.length === 0 && (
            <div className="rounded-xl border border-dashed p-6 text-center text-gray-600">
              Sin avisos. Aquí aparecen los registros fuera de horario, los intentos con foto o celular y la cámara tapada.
            </div>
          )}
          <ul className="space-y-2">{c.d.inmediatos.map((r) => (
            <TarjetaRevision key={r.id_revision} r={{ ...r, id: r.id_revision }} alCambiar={c.recargar} />))}</ul>
        </div>
      )}
    </Marco>
  );
}

export function Rostro({ puede }) {
  const c = useLeer('/asistencia/rostro/config');
  const p = useLeer('/asistencia/rostro/personas');
  const [msg, setMsg] = useState(null);
  const editar = puede('asistencia.horarios');
  async function guardar(cambios) {
    try { const r = await api.put('/asistencia/rostro/config', cambios); setMsg(r.mensaje); c.recargar(); } catch (e) { setMsg(`${e.message} ${e.queHacer || ''}`); }
  }
  async function modo(id, m) {
    try {
      await api.put(`/asistencia/rostro/terminales/${id}/modo`, { modo: m, motivo: 'Revisado por el administrador' });
      setMsg('Modo de la terminal guardado.'); c.recargar();
    } catch (e) { setMsg(`${e.message} ${e.queHacer || ''}`); }
  }
  async function retirar(id) {
    try { const r = await api.post(`/asistencia/rostro/personas/${id}/retiro`, {}); setMsg(r.mensaje); p.recargar(); } catch (e) { setMsg(`${e.message} ${e.queHacer || ''}`); }
  }
  async function formato(id) {
    try { const b = await api.bajar(`/asistencia/rostro/formato/${id}`); window.open(URL.createObjectURL(b), '_blank', 'noopener'); } catch (e) { setMsg(e.message); }
  }
  return (
    <Marco {...c} que="la configuración del rostro">
      {c.d && (
        <div className="space-y-4">
          <section className="space-y-2 rounded-xl border border-gray-200 bg-white p-3">
            <h2 className="font-semibold text-gray-900">Pasar lista con el rostro</h2>
            <p className="text-sm text-gray-600">Solo para quien aceptó por escrito. Quien no acepta pasa lista con PIN o QR, igual que siempre.</p>
            {editar ? (
              <div className="grid gap-2 sm:grid-cols-2">
                <button type="button" className={c.d.config.rostro_activo ? SECUNDARIO : PRIMARIO}
                  onClick={() => guardar({ rostro_activo: !c.d.config.rostro_activo })}>
                  {c.d.config.rostro_activo ? 'Apagar el rostro' : 'Encender el rostro'}
                </button>
                <button type="button" className={SECUNDARIO} onClick={() => guardar({ avisos_horario: !c.d.config.avisos_horario })}>
                  {c.d.config.avisos_horario ? 'Apagar avisos de horario' : 'Avisarme fuera de horario'}
                </button>
              </div>
            ) : <p className="text-sm text-gray-700">{c.d.config.rostro_activo ? 'Encendido' : 'Apagado'}. Lo cambia quien define los horarios.</p>}
            <p className="text-xs text-gray-600">Las fotos de cada registro se borran a los {c.d.config.conservacion_dias} días. Aviso versión {c.d.aviso.version} ({c.d.aviso.estado}).</p>
          </section>
          {c.d.terminales.length > 0 && (
            <section className="space-y-2 rounded-xl border border-gray-200 bg-white p-3">
              <h2 className="font-semibold text-gray-900">Terminales con cámara</h2>
              <ul className="space-y-2">{c.d.terminales.map((t) => (
                <li key={t.id} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                  <span>{t.nombre} · {t.camara_estado === 'OK' ? 'cámara bien' : 'cámara con problema'}{t.modo === 'CONFIRMAR_SIEMPRE' ? ' · pide confirmar cada rostro' : ''}</span>
                  {editar && t.modo !== 'NORMAL' && <button type="button" className={SECUNDARIO} onClick={() => modo(t.id, 'NORMAL')}>Volver a normal</button>}
                </li>))}</ul>
            </section>
          )}
          <Marco {...p} que="las personas">
            {p.d && (
              <section className="space-y-2 rounded-xl border border-gray-200 bg-white p-3">
                <h2 className="font-semibold text-gray-900">Personas</h2>
                <ul className="space-y-2">{p.d.personas.filter((x) => x.activo).map((x) => (
                  <li key={x.id} className="flex flex-wrap items-center justify-between gap-2 text-sm">
                    <span>{x.nombre} · {x.tiene_rostro ? 'rostro registrado' : x.consentimiento ? 'aceptó, falta la captura' : 'sin rostro'}</span>
                    {puede('rostro.registrar') && (
                      <span className="flex gap-2">
                        <button type="button" className={SECUNDARIO} onClick={() => formato(x.id)}>Formato para firmar</button>
                        {x.consentimiento && (
                          <Confirmar texto="Retirar y borrar" alConfirmar={() => retirar(x.id)}
                            consecuencia={`Se borra el rostro de ${x.nombre} del servidor y de todas las terminales; seguirá pasando lista con PIN o QR.`} />)}
                      </span>
                    )}
                  </li>))}</ul>
                {p.d.borrados.length > 0 && (
                  <p className="text-xs text-gray-600">Último borrado: {p.d.borrados[0].persona} · ya lo borraron {p.d.borrados[0].terminales_que_ya_borraron} de {p.d.borrados[0].terminales_con_camara} terminales con cámara.</p>
                )}
              </section>
            )}
          </Marco>
          {msg && <p role="status" className="text-sm">{msg}</p>}
        </div>
      )}
    </Marco>
  );
}

export function MisRegistros() {
  const m = useLeer('/asistencia/mis-registros');
  const yo = useLeer('/asistencia/rostro/mio');
  const [msg, setMsg] = useState(null);
  async function noFuiYo(id) {
    try { const r = await api.post(`/asistencia/mis-registros/${id}/no-fui-yo`, {}); setMsg(r.mensaje); } catch (e) { setMsg(`${e.message} ${e.queHacer || ''}`); }
  }
  async function retirarMio() {
    try { const r = await api.post('/asistencia/rostro/mio/retiro', {}); setMsg(r.mensaje); yo.recargar(); } catch (e) { setMsg(`${e.message} ${e.queHacer || ''}`); }
  }
  return (
    <Marco {...m} que="tus registros">
      {m.d && (
        <div className="space-y-3">
          <p className="text-sm text-gray-600">Tus registros de hoy y con qué se hizo cada uno. Si uno no fuiste tú, márcalo y tu encargado lo revisa.</p>
          {m.d.eventos.length === 0 && <div className="rounded-xl border border-dashed p-6 text-center text-gray-600">Hoy no tienes registros. Pasa lista en la terminal o con tu teléfono.</div>}
          <ul className="space-y-2">{m.d.eventos.map((e) => (
            <li key={e.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl border border-gray-200 bg-white p-3 text-sm">
              <span>{e.hora} · {EVENTOS[e.evento] || e.evento} {e.metodo ? METODOS[e.metodo] : ''}</span>
              <button type="button" className={SECUNDARIO} onClick={() => noFuiYo(e.id)}>Esto no fui yo</button>
            </li>))}</ul>
          {yo.d && (
            <section className="space-y-2 rounded-xl border border-gray-200 bg-white p-3 text-sm">
              <h2 className="font-semibold text-gray-900">Tu rostro</h2>
              <p>{yo.d.tiene_rostro ? 'Tu rostro está registrado para pasar lista.' : 'Tu rostro no está registrado: pasas lista con PIN o QR.'}</p>
              {yo.d.consentimiento && (
                <Confirmar texto="Retirar mi consentimiento" alConfirmar={retirarMio}
                  consecuencia="Tu rostro se borra del servidor y de todas las terminales. Sigues pasando lista con tu PIN o el QR." />)}
            </section>
          )}
          {msg && <p role="status" className="text-sm">{msg}</p>}
        </div>
      )}
    </Marco>
  );
}
